import React from 'react';
import { Space, Tag, Typography } from 'antd';
import {
  CalendarOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  AlertOutlined
} from '@ant-design/icons';
import * as Icons from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

// ステータスのメタ情報
const STATUS_META: Record<
  string,
  { label: string; color: string; icon: React.ReactNode }
> = {
  scheduled: { label: '予定', color: 'blue', icon: <CalendarOutlined /> },
  in_progress: { label: '実行中', color: 'orange', icon: <SyncOutlined spin /> },
  completed: { label: '完了', color: 'green', icon: <CheckCircleOutlined /> },
  cancelled: { label: 'キャンセル', color: 'red', icon: <CloseCircleOutlined /> },
  postponed: { label: '延期', color: 'volcano', icon: <CalendarOutlined /> }
};

// 作業カテゴリのラベル
const WORK_TYPE_LABELS: Record<string, string> = {
  inspection: '点検',
  repair: '修理',
  replacement: '交換',
  maintenance: 'メンテナンス',
  emergency: '緊急作業'
};

export interface ScheduleDisplayProps {
  scheduleId?: string;
  schedule?: {
    id: string;
    title?: string | null;
    displayId?: string | null;
    status?: string | null;
    workType?: string | null;
    workTypeCategory?: {
      id: string;
      categoryLabel: string;
      color?: string | null;
      icon?: string | null;
    } | null;
    scheduleType?: string | null;
    priority?: string | null;
  };
  showStatus?: boolean;
  showWorkType?: boolean;
  showDisplayId?: boolean;
  noLink?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

/**
 * スケジュール情報を統一的に表示するコンポーネント
 *
 * @example
 * // 基本的な使用（リンク付き）
 * <ScheduleDisplay schedule={schedule} />
 *
 * @example
 * // ステータスと作業カテゴリ付き
 * <ScheduleDisplay schedule={schedule} showStatus showWorkType />
 *
 * @example
 * // リンクなし
 * <ScheduleDisplay schedule={schedule} noLink />
 */
export function ScheduleDisplay({
  scheduleId,
  schedule,
  showStatus = false,
  showWorkType = false,
  showDisplayId = false,
  noLink = false,
  size = 'default',
  style
}: ScheduleDisplayProps): JSX.Element | null {
  const navigate = useNavigate();

  // scheduleオブジェクトまたはscheduleIdが必要
  if (!schedule && !scheduleId) {
    return null;
  }

  const effectiveSchedule = schedule || {
    id: scheduleId!,
    title: null,
    displayId: null,
    status: null,
    workType: null,
    scheduleType: null,
    priority: null
  };

  const displayName = effectiveSchedule.title || effectiveSchedule.displayId || effectiveSchedule.id;
  const statusInfo = effectiveSchedule.status
    ? STATUS_META[effectiveSchedule.status] || {
      label: effectiveSchedule.status,
      color: 'default',
      icon: <CalendarOutlined />
    }
    : null;

  // workTypeCategory を優先、なければ従来の workType から取得
  const workTypeLabel = effectiveSchedule.workTypeCategory?.categoryLabel ??
    (effectiveSchedule.workType
      ? WORK_TYPE_LABELS[effectiveSchedule.workType] || effectiveSchedule.workType
      : null);

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const iconKey = iconName as keyof typeof Icons;
    const IconComponent = Icons[iconKey] as React.ComponentType<{ style?: React.CSSProperties }> | undefined;
    return IconComponent ? <IconComponent style={{ fontSize: fontSize - 2 }} /> : null;
  };

  const isEmergency = effectiveSchedule.scheduleType === 'emergency';

  const handleClick = () => {
    if (!noLink && effectiveSchedule.id) {
      navigate(`/app/schedule/${effectiveSchedule.id}`);
    }
  };

  const fontSize = size === 'small' ? 12 : size === 'large' ? 16 : 14;

  return (
    <Space size={8} style={{ cursor: noLink ? 'default' : 'pointer', ...style }} wrap>
      {isEmergency && (
        <AlertOutlined style={{ color: '#ff4d4f', fontSize: fontSize + 2 }} />
      )}
      {noLink ? (
        <Text strong style={{ fontSize }}>
          {displayName}
        </Text>
      ) : (
        <Text onClick={handleClick} strong style={{ fontSize, cursor: 'pointer' }}>
          {displayName}
        </Text>
      )}
      {showDisplayId && effectiveSchedule.displayId && effectiveSchedule.displayId !== displayName && (
        <Text type="secondary" style={{ fontSize: fontSize - 2 }}>
          ({effectiveSchedule.displayId})
        </Text>
      )}
      {showStatus && statusInfo && (
        <Tag color={statusInfo.color} icon={statusInfo.icon} style={{ fontSize: fontSize - 2 }}>
          {statusInfo.label}
        </Tag>
      )}
      {showWorkType && workTypeLabel && (
        <Tag
          color={effectiveSchedule.workTypeCategory?.color || undefined}
          icon={getIconComponent(effectiveSchedule.workTypeCategory?.icon)}
          style={{ fontSize: fontSize - 2 }}
        >
          {workTypeLabel}
        </Tag>
      )}
    </Space>
  );
}
